import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BadgeCheck, Sprout, BedDouble, MessageSquare, Footprints, Zap, Mail, UserPlus, ShoppingBag, Newspaper, Target, Smartphone, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { C, FONT, fmtWaktu } from "./ui";
import { Sheet, Empty } from "./Sheet";
import { TOKEN_KEY, readProfile, Verifikasi } from "./PesanSheet";

// Ikon & warna lingkaran per jenis kabar (ala referensi: tiap jenis punya warna sendiri)
const IKON = {
  panen: [Sprout, "#2e9e5b"], istirahat: [BedDouble, "#0f9ba8"], buku_tamu: [MessageSquare, "#2563eb"], tamu: [Footprints, "#189a5b"],
  energi: [Zap, "#f4b400"], surat: [Mail, "#e11d48"], teman: [UserPlus, "#7c3aed"], mall: [ShoppingBag, "#d9467a"], koran: [Newspaper, "#2563eb"], misi: [Target, "#7c3aed"],
};

// Baris kabar: lingkaran ikon berwarna · judul tebal · sub (opsional) · waktu · titik merah bila baru
function BarisKabar({ k, onClick }) {
  const [Icon, warna] = IKON[k.ikon] || [Bell, C.sub];
  return (
    <button data-testid={`kabar-${k.key}`} onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 2px", border: 0, borderTop: `1px solid ${C.line}`, background: "transparent", cursor: k.tujuan ? "pointer" : "default", textAlign: "left", fontFamily: FONT, color: C.ink }}>
      <span style={{ width: 38, height: 38, borderRadius: 99, background: warna, color: "#fff", display: "grid", placeItems: "center", flex: "none" }}><Icon size={17} strokeWidth={2.2} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <b style={{ display: "block", fontSize: 13.5, lineHeight: 1.3 }}>{k.judul}</b>
        {k.sub && <span style={{ display: "block", fontSize: 11.5, color: C.sub, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{k.sub}</span>}
        <span style={{ display: "block", fontSize: 11, color: C.sub, marginTop: 2 }}>{fmtWaktu(k.at)}</span>
      </span>
      {k.baru ? <span data-testid={`kabar-${k.key}-baru`} style={{ width: 8, height: 8, borderRadius: 99, background: "#e11d48", flex: "none" }} /> : k.tujuan && <ChevronRight size={15} color={C.line} />}
    </button>
  );
}

// Sheet "Kabar untukmu" (ala referensi, gaya Jawsel): tip pasang di layar utama + daftar kabar pribadi (panen, energi, surat,
// teman, tamu, Mall, koran, misi). Membuka sheet menandai semua sudah dilihat (titik merah tetap tampil di sesi ini).
export function KabarSheet({ cityId, onClose }) {
  const nav = useNavigate();
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const warga = readProfile();
  const [verif, setVerif] = useState(false), [d, setD] = useState(null);
  const ditandai = useRef(false);
  useEffect(() => {
    if (!token) return;
    api.kabar(token).then(x => { setD(x); if (!ditandai.current) { ditandai.current = true; api.kabarLihat(token).catch(() => {}); } }).catch(() => setD({ kabar: [] }));
  }, [token]);
  if (verif) return <Verifikasi cityId={cityId} onBack={() => setVerif(false)} onClose={onClose} />;

  const buka = k => {
    if (!k.tujuan) return;
    if (k.tujuan.startsWith("?")) { onClose(); nav(`/kota/${cityId}${k.tujuan}`); return; }
    nav(k.tujuan.includes("?") ? k.tujuan : `${k.tujuan}${k.tujuan === "/kerja" || k.tujuan === "/mall" ? `?kota=${cityId}` : ""}`);
  };
  const iOS = /iPhone|iPad/i.test(navigator.userAgent);
  return (
    <Sheet testid="kabar-sheet" title="Kabar untukmu" sub={warga ? `@${warga.ig_username}` : "Pemberitahuan pribadi"} icon={Bell} onClose={onClose}>
      {!token && (
        <div style={{ textAlign: "center" }}>
          <Empty icon={Bell} testid="kabar-tanpa-kartu" text="Kabar pribadi hanya untuk pemegang Kartu Warga: panen siap, surat baru, energi penuh, dan barang baru di Mall." />
          <button data-testid="kabar-jadi-warga" onClick={() => setVerif(true)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "13px 18px", border: 0, borderRadius: 99, background: C.blue, color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: FONT }}><BadgeCheck size={17} /> Jadi Warga Jawsel</button>
        </div>
      )}
      {token && (
        <>
          <div data-testid="kabar-tip-hp" style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 0 11px" }}>
            <span style={{ width: 38, height: 38, borderRadius: 99, background: C.blueSoft, color: C.blue, display: "grid", placeItems: "center", flex: "none" }}><Smartphone size={17} /></span>
            <span style={{ minWidth: 0 }}><b style={{ display: "block", fontSize: 13.5 }}>Pasang Jawsel di layar utama HP</b><span style={{ display: "block", fontSize: 11.5, color: C.sub, lineHeight: 1.4 }}>{iOS ? "Di iPhone: Bagikan → Tambah ke Layar Utama" : "Di Android: menu ⋮ → Tambahkan ke layar utama"} — kabar lebih mudah dicek kapan saja.</span></span>
          </div>
          {!d && <p style={{ margin: "14px 0", fontSize: 12.5, color: C.sub }}>Memuat kabar…</p>}
          {d && d.kabar.length === 0 && <Empty icon={Bell} testid="kabar-kosong" text="Belum ada kabar. Tanam bibit, kirim surat, atau bekerja — kabarnya akan muncul di sini." />}
          <div data-testid="kabar-daftar">{(d?.kabar || []).map(k => <BarisKabar key={k.key} k={k} onClick={() => buka(k)} />)}</div>
        </>
      )}
    </Sheet>
  );
}
